import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { IFormFieldType } from "../../../types/api/dynamic-form";
import { createField, deleteField, detailFiled, fieldList } from "./callbacks";

export const useFormFieldSave = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IFormFieldType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IFormFieldType>,
    unknown,
    IFormFieldType
  > = {}
): UseMutationResult<TResponse<IFormFieldType>, unknown, IFormFieldType> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IFormFieldType>, unknown, IFormFieldType>({
    mutationFn: async (data: IFormFieldType) => {
      const response = await createField(data);
      return response.data;
    },
    onSuccess: (res: TResponse<IFormFieldType>, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.DYNAMIC_FORM_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const exists = old.data.find((v: any) => v._id === res.data._id);
          return {
            ...old,
            data: exists
              ? old.data.map((v: any) =>
                  v._id === res.data._id ? res.data : v
                )
              : [res.data, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useFormFieldList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IFormFieldType[]>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponseWithQData<IFormFieldType[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IFormFieldType[]>, Error>({
    queryKey: [
      API_ENDPOINTS.DYNAMIC_FORM_LIST,
      { ...queryParams, total: null },
    ],
    queryFn: async () => {
      const res = await fieldList(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useFormFieldDetail = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IFormFieldType>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IFormFieldType>, Error> => {
  return useQuery<TResponse<IFormFieldType>, Error>({
    queryKey: [API_ENDPOINTS.DYNAMIC_FORM_DETAIL, id],
    queryFn: async () => {
      const res = await detailFiled(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

export const useFormFieldDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IFormFieldType>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IFormFieldType>, unknown, string> = {}
): UseMutationResult<TResponse<IFormFieldType>, unknown, string> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IFormFieldType>, unknown, string>({
    mutationFn: async (_id: string) => {
      const response = await deleteField(_id);
      return response.data;
    },
    onSuccess: (res: TResponse<IFormFieldType>, ...rest) => {
      const _id = res.data?._id;
      queryClient.setQueryData(
        [API_ENDPOINTS.DYNAMIC_FORM_LIST, { ...qData, total: null }],
        (old: TResponse<IFormFieldType[]>) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.filter((v) => v._id !== _id),
          };
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};
